// Main entry point for the ERE-Vergelijk application
import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import App from './App.tsx';
import { PrerenderProvider, type PrerenderData } from './contexts/PrerenderContext';
import { installGlobalClientErrorHandlers, reportClientError } from './lib/clientErrorReport';
import { installStaleBundleReload, isChunkLoadError } from './lib/staleBundle';
import './index.css';

declare global {
  interface Window {
    __PRERENDER_DATA__?: PrerenderData;
  }
}

installGlobalClientErrorHandlers();
// Before the router and before hydrateRoot: a chunk that goes missing while
// the first render still runs is caught too.
installStaleBundleReload();

const rootEl = document.getElementById('root')!;
const prerenderData = window.__PRERENDER_DATA__ ?? null;

// We use the data router (createBrowserRouter) on the client because the
// admin panel relies on `useBlocker` for unsaved-changes protection — that
// hook only works under RouterProvider. On the server, entry-server.tsx
// uses <StaticRouter> instead; that path never renders the admin panel.
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <PrerenderProvider value={prerenderData}>
        <App />
      </PrerenderProvider>
    ),
  },
]);

const tree = (
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
);

/**
 * Hydration mismatches never reach an error boundary — React "recovers" by
 * throwing the server HTML away and client-rendering the whole tree, so the
 * page looks fine to whoever is testing it and the only trace is a console
 * warning in the visitor's browser. That is precisely how the #418 mismatches
 * on About and the homepage stayed invisible in production while every
 * prerendered page silently lost its SSR benefit.
 *
 * onRecoverableError is the only hook that fires for them. Reporting here turns
 * the class of bug that is hardest to notice into a dashboard entry naming the
 * page it happened on.
 */
const rootOptions = {
  onRecoverableError: (error: unknown, errorInfo: { componentStack?: string | null }) => {
    // A lazy page chunk that fails during hydration arrives here as #520
    // with the import's error as its cause; the boundary has reported it
    // already, as `chunk`, so it is one incident and one row.
    if (isChunkLoadError(error)) return;
    const err = error instanceof Error ? error : undefined;
    reportClientError({
      kind: 'hydration',
      message: err?.message ?? String(error),
      stack: err?.stack,
      componentStack: errorInfo?.componentStack ?? undefined,
    });
  },
};

if (prerenderData) {
  hydrateRoot(rootEl, tree, rootOptions);
} else {
  createRoot(rootEl, rootOptions).render(tree);
}
